import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeSlash, ChatCircle, Trash, ArrowLeft, ShieldCheck } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import api from '../lib/api';

export default function SettingsPage() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [profileVisible, setProfileVisible] = useState(user?.profile_visible ?? true);
  const [messageFilter, setMessageFilter] = useState(user?.message_filter || 'all');

  if (!user) { navigate('/auth'); return null; }

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/settings', {
        profile_visible: profileVisible,
        message_filter: messageFilter
      });
      updateUser(data.user);
    } catch {}
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) return;
    try {
      await api.delete('/account');
      logout();
      navigate('/');
    } catch {}
  };

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="settings-page">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-6 transition-colors">
          <ArrowLeft size={20} /> Back
        </button>

        <h1 className="text-2xl md:text-3xl font-medium tracking-tight text-white font-['Outfit'] mb-8">Settings</h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Profile Visibility */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {profileVisible ? <Eye size={24} weight="light" className="text-[#D4AF37]" /> : <EyeSlash size={24} weight="light" className="text-[#9EABCE]" />}
                <div>
                  <p className="text-white font-medium">Profile Visibility</p>
                  <p className="text-[#9EABCE] text-xs mt-0.5">{profileVisible ? 'Your profile is visible to others' : 'Your profile is hidden'}</p>
                </div>
              </div>
              <Switch
                checked={profileVisible}
                onCheckedChange={setProfileVisible}
                data-testid="settings-visibility-toggle"
                className="data-[state=checked]:bg-[#D4AF37]"
              />
            </div>
          </div>

          {/* Message Filter */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <ChatCircle size={24} weight="light" className="text-[#D4AF37]" />
              <div>
                <p className="text-white font-medium">Message Filter</p>
                <p className="text-[#9EABCE] text-xs mt-0.5">Choose who can send you messages</p>
              </div>
            </div>
            <Select value={messageFilter} onValueChange={setMessageFilter}>
              <SelectTrigger data-testid="settings-message-filter" className="bg-white/5 border-white/10 text-white rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B112B] border-white/10">
                <SelectItem value="all" className="text-white">All Users</SelectItem>
                <SelectItem value="verified" className="text-white">Verified Users Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Premium Status */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheck size={24} weight="light" className="text-[#D4AF37]" />
                <div>
                  <p className="text-white font-medium">Premium Status</p>
                  <p className="text-[#9EABCE] text-xs mt-0.5">
                    {user.is_premium ? 'Active Premium Member' : 'Free plan - 10 chats/day'}
                  </p>
                </div>
              </div>
              {!user.is_premium && (
                <button
                  onClick={() => navigate('/premium')}
                  data-testid="settings-upgrade-btn"
                  className="btn-gold px-4 py-2 text-sm rounded-xl font-bold"
                >
                  Upgrade
                </button>
              )}
            </div>
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="settings-save-btn"
            className="w-full btn-gold py-3 rounded-xl font-bold disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>

          {/* Delete Account */}
          <div className="glass-card p-6 border-red-500/10">
            <div className="flex items-center gap-3 mb-4">
              <Trash size={24} weight="light" className="text-red-400" />
              <div>
                <p className="text-white font-medium">Delete Account</p>
                <p className="text-[#9EABCE] text-xs mt-0.5">Permanently delete your account and all data</p>
              </div>
            </div>
            <button
              onClick={handleDeleteAccount}
              data-testid="settings-delete-account-btn"
              className="w-full py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-semibold hover:bg-red-500/20 transition-all border border-red-500/20"
            >
              Delete My Account
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
